import includes from "lodash/includes";
import some from "lodash/some";
import { PureComponent } from "react";
import { matchPath, RouteComponentProps, RouteProps } from "react-router";
import { ExportType } from "@app/exports/enumerations";
import { Module } from "@app/exports/literals";
import { Forbidden } from "@app/shared/ErrorPages";
import Roles from "@app/user/roles";
import { UserInfo, UserContextProps } from "@app/user/UserContext";

interface RouteRestrictionBoundaryRouteProps extends RouteComponentProps<unknown> {
  user: UserInfo;
}

interface ExportRestriction extends RoleRestriction {
  exportType: ExportType;
  module: Module;
}

interface RoleRestriction {
  allowedRoles?: Roles[];
  deniedRoles?: Roles[];
}

interface RouteRestriction extends RoleRestriction {
  routeProps: RouteProps;
}

/**
 * Renders a forbidden component if the user is restricted from accessing the current route...needless to say, this needs to be used somewhere inside a router
 */
class RouteRestrictionBoundary extends PureComponent<React.PropsWithChildren<RouteRestrictionBoundaryRouteProps & RoleRestriction & UserContextProps>> {
  public render() {
    return (
      <>
        {!!(isPathnameRestricted(this.props.location.pathname, this.props.user))
          ? <Forbidden />
          : this.props.children
        }
      </>
    );
  }
}

/**
 * Determines whether or not the @param user is restricted from accessing the given @param exportType under the given @param module
 * @param exportType The export-type to check if the user is restricted
 * @param module The module under which the export-type should be checked
 * @param user The user whose roles should be checked to determine access
 */
function isExportRestricted(exportType: ExportType, module: Module, user: UserInfo): boolean {
  return exportRestrictions.some((er) => (
    (er.exportType === exportType && er.module === module && isUserRestricted(er.allowedRoles, er.deniedRoles, user))
  ));
}

/**
 * Determines whether or not the @param user is restricted from accessing the given @param pathname
 * @param pathname The pathname to check if the user is restricted
 * @param user The user whose roles should be checked to determine access
 */
function isPathnameRestricted(pathname: string, user: UserInfo): boolean {
  return routeRestrictions.some((rr) => (
    (matchPath(pathname, rr.routeProps) && isUserRestricted(rr.allowedRoles, rr.deniedRoles, user))
  ));
}

/**
 * Determines whether or not the @param user is restricted given the @param allowedRoles & @param deniedRoles
 * @param allowedRoles The roles that, if a user has any of, will have the user be considered not-restricted
 * @param deniedRoles  The roles that, if a user has any of (regardless of allowed-roles), will have the user be considered restricted
 * @param user The user whose roles should be compared to the allowed and denied roles
 */
function isUserRestricted(allowedRoles: Roles[], deniedRoles: Roles[], user: UserInfo): boolean {
  //we'll do some checks and if anything is not what it's expected to be, we take the safe route and restrict access
  if (!user || !user.roles || user.roles.length === 0) {
    return true;
  }
  allowedRoles = allowedRoles || []; deniedRoles = deniedRoles || [];
  if (allowedRoles.length === 0 && deniedRoles.length === 0) {
    return true;
  }

  const hasAllowed = some(user.roles, (role) => includes(allowedRoles, role));
  const hasDenied = some(user.roles, (role) => includes(deniedRoles, role));

  return !(!hasDenied && (hasAllowed || allowedRoles.length === 0));
}

/**
 * Static restriction rules surrounding exports
 */
const exportRestrictions: ExportRestriction[] = [
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    exportType: ExportType.ActionableProviders,
    module: "accuracy",
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    exportType: ExportType.ServicingProviders,
    module: "accuracy",
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    exportType: ExportType.StatusReport,
    module: "accuracy",
  },
];

/**
 * Static restriction rules surrounding routes
 */
const routeRestrictions: RouteRestriction[] = [
  // try to keep this SORTED BY PATH (routeProps.path) to aid in
  // troubleshooting route-restrictions (should the need arise)
  {
    allowedRoles: [Roles.QUEST_ADMIN],
    deniedRoles: [],
    routeProps: {
      exact: false,
      path: `/access-reports/wizard-builder`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCESS_REPORTS_VIEWER],
    deniedRoles: [],
    routeProps: {
      exact: false,
      path: `/access-reports`,
      strict: false,
    },
  },
  {
    allowedRoles: [],
    deniedRoles: [Roles.SOFTWARE_LICENSE, Roles.TOKEN_USER],
    routeProps: {
      exact: false,
      path: `/account/manage`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.QUEST_ADMIN],
    deniedRoles: [],
    routeProps: {
      exact: false,
      path: `/admin/clients`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.CLIENT_ADMIN],
    deniedRoles: [],
    routeProps: {
      exact: false,
      path: `/admin/results/providerResults`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.QUEST_ADMIN],
    deniedRoles: [],
    routeProps: {
      exact: false,
      path: `/admin/users/sessions`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: false,
      path: `/projects/:projectId/accuracy/inspection/:inspectionUid`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/state/:stateRegionCode/county/:countyRegionCode/inspection/:inspectionUid`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/state/:stateRegionCode/county/:countyRegionCode/specialty/:specialtyGroupCode`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/state/:stateRegionCode/specialty/:specialtyGroupCode/county/:countyRegionCode`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/specialty/:specialtyGroupCode/county/:countyRegionCode`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/specialty/:specialtyGroupCode/state/:stateRegionCode/county/:countyRegionCode`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/county/:countyRegionCode/specialty/:specialtyGroupCode`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/state/:stateRegionCode/specialty/:specialtyGroupCode/inspection/:inspectionUid`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/state/:stateRegionCode/inspection/:inspectionUid`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/specialty/:specialtyGroupCode/state/:stateRegionCode/inspection/:inspectionUid`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/specialty/:specialtyGroupCode/inspection/:inspectionUid`,
      strict: false,
    },
  },
  {
    allowedRoles: [Roles.ACCURACY_LICENSE_TIER_2],
    deniedRoles: [],
    routeProps: {
      exact: true,
      path: `/projects/:projectId/accuracy/county/:countyRegionCode/inspection/:inspectionUid`,
      strict: false,
    },
  },
];

export { isExportRestricted, isPathnameRestricted, isUserRestricted, RouteRestrictionBoundary };
